import { ErrorNotice } from '@brush/ui';
import { Plus, Shield } from 'lucide-react';
import { useState } from 'react';
import { summarise, type TournamentFilter } from '../../tournamentFilter';
import { btnGhost, btnPrimary, Modal, modalControl } from '../Modal';
import { changedFrom } from '../../dialogDrafts';
import { useUnsavedWork } from '../../unsaved';

/** What this apply does to one tag: add it everywhere, take it off everywhere, or leave it. */
type Change = 'none' | 'add' | 'remove';

const CHIP: Record<Change, string> = {
  none: 'border border-ink-800/15 text-ink-800/70 hover:border-felt-500',
  add: 'border border-felt-700 bg-felt-700 font-medium text-white',
  remove:
    'border border-ink-800/40 bg-ink-950/5 text-ink-800/60 line-through decoration-ink-800/50',
};

const LABEL: Record<Change, (tag: string) => string> = {
  none: (tag) => `${tag}: unchanged. Activate to add it to every matched tournament.`,
  add: (tag) => `${tag}: being added. Activate to remove it instead.`,
  remove: (tag) => `${tag}: being removed. Activate to leave it alone.`,
};

/**
 * Applying tags to everything the filter matched, rather than one tournament at a time.
 *
 * The same three-way chip the filter bar uses, because it is the same question in a different
 * mood — there "required / excluded / either", here "add / remove / leave alone". A platform tag
 * has only two of the three: locked rule 5 says a room cannot take one off, so its chip never
 * reaches `remove` and the server refuses it regardless.
 *
 * The count in the button is the whole guard. This writes to rows the operator cannot see —
 * pages beyond the first — so the number is the only thing standing between "tag these six" and
 * "tag these six hundred", and it is stated where the click happens rather than in a paragraph
 * above it.
 */
export function BulkTagModal({
  filter,
  matched,
  allTags,
  platformTags,
  saving,
  errorMessage,
  onApply,
  onClose,
}: {
  filter: TournamentFilter;
  matched: number;
  allTags: string[];
  platformTags: string[];
  saving: boolean;
  errorMessage: string | null;
  onApply: (add: string[], remove: string[]) => void;
  onClose: () => void;
}) {
  const [changes, setChanges] = useState<Record<string, Change>>({});
  const [draft, setDraft] = useState('');
  // Chosen changes, or a tag half typed, are asked about before a room switch discards them (#591).
  useUnsavedWork(changedFrom({ changes: {}, draft: '' }, { changes, draft: draft.trim() }));

  const isPlatform = (tag: string) => platformTags.some((p) => p.toLowerCase() === tag.toLowerCase());

  // Tags typed in here rather than picked, kept separate so the vocabulary row stays the room's
  // actual vocabulary. A new tag can only ever be added, never removed.
  const [invented, setInvented] = useState<string[]>([]);

  const stateOf = (tag: string): Change => changes[tag] ?? 'none';

  const cycle = (tag: string) => {
    // Platform tags skip `remove` entirely: offering a state the server rejects would be a
    // control the UI pretends to have.
    const order: Change[] = isPlatform(tag) ? ['none', 'add'] : ['none', 'add', 'remove'];
    const next = order[(order.indexOf(stateOf(tag)) + 1) % order.length]!;
    setChanges((c) => ({ ...c, [tag]: next }));
  };

  const addInvented = () => {
    const tag = draft.trim();
    if (tag === '') return;
    const known = [...allTags, ...invented].some((t) => t.toLowerCase() === tag.toLowerCase());
    if (!known) setInvented((v) => [...v, tag]);
    setChanges((c) => ({ ...c, [tag]: 'add' }));
    setDraft('');
  };

  const vocabulary = [...allTags, ...invented];
  const add = vocabulary.filter((t) => stateOf(t) === 'add');
  const remove = vocabulary.filter((t) => stateOf(t) === 'remove');
  const nothingToDo = add.length === 0 && remove.length === 0;

  const chips = summarise(filter);

  return (
    <Modal
      title="Tag filtered tournaments"
      subtitle={
        chips.length > 0 ? `Matching ${chips.join(' · ')}` : 'Every tournament in this room'
      }
      onClose={onClose}
      footer={
        <>
          <button type="button" onClick={onClose} className={btnGhost}>
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onApply(add, remove)}
            disabled={saving || nothingToDo || matched === 0}
            className={btnPrimary}
          >
            {saving
              ? 'Applying…'
              : `Apply to ${matched.toLocaleString()} ${matched === 1 ? 'tournament' : 'tournaments'}`}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {vocabulary.map((tag) => {
            const state = stateOf(tag);
            return (
              <button
                key={tag}
                type="button"
                onClick={() => cycle(tag)}
                aria-label={LABEL[state](tag)}
                className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs ${CHIP[state]}`}
              >
                {isPlatform(tag) ? <Shield className="size-2.5" /> : null}
                {state === 'remove' ? '− ' : ''}
                {tag}
              </button>
            );
          })}
          {vocabulary.length === 0 ? (
            <span className="text-xs text-ink-800/50">This room has no tags yet.</span>
          ) : null}
        </div>

        <div className="flex items-center gap-2">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                addInvented();
              }
            }}
            placeholder="New tag…"
            aria-label="Add a tag that does not exist yet"
            className={`w-48 ${modalControl}`}
          />
          <button
            type="button"
            onClick={addInvented}
            disabled={draft.trim() === ''}
            className="inline-flex items-center gap-1 border border-ink-800/15 px-2.5 py-1.5 text-xs text-ink-800 transition hover:bg-felt-50 disabled:opacity-40"
          >
            <Plus className="size-3.5" /> Add
          </button>
        </div>

        <p className="text-[11px] text-ink-800/50">
          Click once to add a tag to every matched tournament, twice to take it off. Platform tags
          carry a shield and can only be added — rooms can&apos;t remove them.
        </p>

        {/* Says plainly that this reaches past the page on screen, which is the one thing about
            a bulk action that is easy to get wrong. */}
        <p className="text-[11px] text-ink-800/50">
          This applies to every tournament the filter matches, including the ones on later pages.
        </p>

        <ErrorNotice message={errorMessage} />
      </div>
    </Modal>
  );
}
